import * as x509 from '@peculiar/x509';
import authService, { OperatorSession, SavedTokens } from './AuthService';

export interface GetRotateKeysResponseData {
  trustee_stem_id_status: RotationStatusInfoJsonData;
  trustee_stem_cell_key_last_update_timestamp?: Date;
  entity_trustee_stem_id_status?: RotationStatusInfoJsonData;
  entity_trustee_entity_stem_ids_status?: RotationStatusRunningInfoJsonData;
  entity_trustee_stem_cell_key_last_update_timestamp?: Date;
}

interface RotationStatusInfoJsonData {
  current_version: number;
  rotation_status?: RotationStatusRunningInfoJsonData;
  stem_id_last_update_timestamp?: Date;
}

interface RotationStatusRunningInfoJsonData {
  node_rotating: string;
  started_at_timestamp: string;
  last_status_timestamp: string;
}

export interface ConfigJsonData {
  name: string;
  value?: string;
  is_secret: boolean;
}

export interface ConfigReplyJsonData {
  config: ConfigJsonData[];
}

// `owner` is the GitHub repository owner. `organization` is the mesh organization that owns the
// build, which is a different thing: absent or "Unresolved" means it could not be determined,
// "Unaffiliated" means there genuinely is none (a personal site), and { Domain } names it.
export type BuildOrganizationJsonData = 'Unresolved' | 'Unaffiliated' | { Domain: string };

export interface BuildInformationJsonData {
  repository_url: string;
  branch: string;
  owner: string;
  repo: string;
  commit_sha: string;
  commit_message: string;
  create_time: number;
  build_start_time?: string;
  status: string;
  node_building?: string;
  last_status_update?: string;
  last_status_message?: string;
  site_id?: string;
  meshagent_deployable_id?: string;
  meshvm_deployable_id?: string;
  organization?: BuildOrganizationJsonData;
}

// A full page carries next_offset; its absence means the queue is exhausted. It cannot distinguish
// a genuinely full page from one truncated by the server's page cap, so treat it only as "ask again
// from here".
export interface GetBuildsResponseJsonData {
  builds: BuildInformationJsonData[];
  next_offset?: number;
}

export function buildOrganizationLabel(organization?: BuildOrganizationJsonData): string {
  if (organization === undefined || organization === 'Unresolved') {
    return '—';
  }
  if (organization === 'Unaffiliated') {
    return 'Personal';
  }
  return organization.Domain;
}

export type DebugSessionStatusJsonData = 'Pending' | 'Running' | 'Complete' | 'Failed';

export interface DebugSessionInformationJsonData {
  session_id: string;
  create_time: string;
  start_time?: string;
  status: DebugSessionStatusJsonData;
  node_running?: string;
  last_status_update: string;
  last_status_message?: string;
  prompt?: string;
  issue_url?: string;
}

export interface CreateDebugSessionResponseJsonData {
  session_id: string;
}

export interface DeploymentContainerStatusJsonData {
  alive: boolean;
  last_failure_reason?: string;
  num_restarts: number;
  create_time?: string;
  last_start_time?: string;
  last_exit_time?: string;
  container_id: string;
  container_name?: string;
}

export interface DeploymentStatusJsonData {
  actor_id: string;
  actor_name: string;
  container_repo: string;
  container_version: string;
  container_status?: DeploymentContainerStatusJsonData;
}

export interface DeploymentStatusesJsonData {
  host: string;
  statuses: DeploymentStatusJsonData[];
  update_time: string;
}

// One orgmember proxy the signed-in human holds. `operator_proxy` is the session link code that
// names it on every operator request; `is_mesh_operator` is advisory and nothing may gate on it.
export interface OperatorProxyRecord {
  operator_proxy: string;
  organization_name?: string;
  organization_public_id?: string;
  is_mesh_operator: boolean;
}

// The organization behind a proxy. `lcode` is the `{lcode}` path segment of every operator route:
// a session link code scoped to this session and this proxy, not an organization id.
export interface OperatorOrganizationRecord {
  lcode: string;
  name: string;
  public_id?: string;
}

// Thrown when more than one of the human's proxies could plausibly carry operator authority. The
// caller has to pick, because which organization the authority is claimed from is a property of
// the operation and not something this client can infer.
export class OperatorProxySelectionRequired extends Error {
  readonly proxies: OperatorProxyRecord[];

  constructor(proxies: OperatorProxyRecord[]) {
    super('Select the organization to act as a mesh operator for.');
    this.name = 'OperatorProxySelectionRequired';
    // Restores the prototype chain, which the ES5 target that react-scripts compiles to breaks for
    // subclasses of built-ins; without it `instanceof` is false at every call site.
    Object.setPrototypeOf(this, OperatorProxySelectionRequired.prototype);
    this.proxies = proxies;
  }
}

export interface CertificateRecordIntermediateData {
  cert: string;
  display_name: string;
  network_location?: string;
}

export interface CertificateRecordExportData {
  display_name: string;
  serial_number: string;
  subject: string;
  pubkey_algorithm_name: string;
  pubkey_algorithm_additional_info: string;
  signature_algorithm_hash: string;
  creation: Date;
  expiration: Date;
  parent_display_name: string;
  parent_serial_number: string;
}

export interface CertificateRecordKey {
  display_name: string;
  serial_number: string;
}

export interface CertificateRecords {
  guardians: Record<string, CertificateRecordExportData>;
  end_entities: Record<string, CertificateRecordExportData>;
  intermediates: Record<string, CertificateRecordExportData>;
  root: Record<string, CertificateRecordExportData>;
}

export function keyToString(key: CertificateRecordKey): string {
  return `${key.display_name} (${key.serial_number})`;
}

class MeshApi {
  private resolving: Promise<OperatorSession> | null = null;

  // Lists every orgmember proxy the signed-in human holds, operator or not.
  //
  // The one operator route that takes no organization code: a client cannot have one before this
  // runs. `is_mesh_operator` on each record is advisory - it is derived from cached orgmember data
  // that can lag the organization agent's own decision - so it orders the choice here rather than
  // filtering it, and the organization agent remains the only thing that decides.
  async listOperatorProxies(): Promise<OperatorProxyRecord[]> {
    let tokens = authService.getTokens();
    if (!tokens) {
      throw new Error('No tokens available');
    }
    let urlBase = process.env.REACT_APP_MESH_API_URL;
    let response = await fetch(`${urlBase}/entities/operator/proxies`, {
      headers: {
        Authorization: `Bearer ${tokens.idToken}`,
      },
    });
    return (await MeshApi.checkedJson(response)) as OperatorProxyRecord[];
  }

  // Resolves the organization a proxy belongs to, and with it the `{lcode}` every operator route
  // addresses. The code is minted for this proxy on this session, so the proxy has to be named
  // before the question can be asked.
  async getOperatorOrganization(operatorProxy: string): Promise<OperatorOrganizationRecord> {
    let tokens = authService.getTokens();
    if (!tokens) {
      throw new Error('No tokens available');
    }
    let urlBase = process.env.REACT_APP_MESH_API_URL;
    let response = await fetch(`${urlBase}/entities/operator/${encodeURIComponent(operatorProxy)}/organization`, {
      headers: {
        Authorization: `Bearer ${tokens.idToken}`,
      },
    });
    return (await MeshApi.checkedJson(response)) as OperatorOrganizationRecord;
  }

  // Binds the session to one proxy and caches the organization code it resolves to.
  async selectOperatorProxy(operatorProxy: string): Promise<OperatorSession> {
    let tokens = authService.getTokens();
    if (!tokens) {
      throw new Error('No tokens available');
    }
    let organization = await this.getOperatorOrganization(operatorProxy);
    let session: OperatorSession = {
      operatorProxy,
      orgLinkCode: organization.lcode,
      organizationName: organization.name,
    };
    authService.setOperatorSession(tokens, session);
    return session;
  }

  // The session every operator call is made through, resolved once per sign-in.
  //
  // Auto-selects only when the choice is unambiguous: exactly one proxy carries the operator flag,
  // or the human holds exactly one proxy at all. The second case matters because the flag is
  // advisory and can read stale `false` while the credential is still propagating, and refusing to
  // act on a sole proxy for that reason would make a real operator's only proxy unreachable.
  // Anything else is a genuine choice of which organization's authority to claim, so it is thrown
  // back for the operator to make rather than picked arbitrarily here.
  async ensureOperatorSession(): Promise<OperatorSession> {
    let tokens = authService.getTokens();
    if (!tokens) {
      throw new Error('No tokens available');
    }
    if (tokens.operatorSession) {
      return tokens.operatorSession;
    }
    // Pages load in parallel and a page can issue several calls at once, so without this each of
    // them would run discovery and mint its own organization code for the same proxy.
    if (!this.resolving) {
      this.resolving = this.resolveOperatorSession().finally(() => {
        this.resolving = null;
      });
    }
    return await this.resolving;
  }

  private async resolveOperatorSession(): Promise<OperatorSession> {
    let proxies = await this.listOperatorProxies();
    if (proxies.length === 0) {
      throw new Error('This account holds no organization membership, so it cannot act as a mesh operator.');
    }
    let operators = proxies.filter(proxy => proxy.is_mesh_operator);
    let chosen = operators.length === 1 ? operators[0] : proxies.length === 1 ? proxies[0] : null;
    if (!chosen) {
      throw new OperatorProxySelectionRequired(proxies);
    }
    return await this.selectOperatorProxy(chosen.operator_proxy);
  }

  clearOperatorSession() {
    let tokens = authService.getTokens();
    if (tokens) {
      authService.clearOperatorSession(tokens);
    }
  }

  // Every operator operation goes out through here: `{lcode}` names the organization the request
  // is sent to, and `operator_proxy` names the proxy the session acts as. The organization agent
  // checks both halves of the authority on every call and relays to the agent named by `target`.
  private async operatorRequest(
    method: string,
    target: 'factory' | 'human',
    op: string,
    body?: unknown,
  ): Promise<Response> {
    let tokens = authService.getTokens();
    if (!tokens) {
      throw new Error('No tokens available');
    }
    let cached = tokens.operatorSession !== undefined;
    let session = await this.ensureOperatorSession();
    let response = await this.sendOperatorRequest(tokens, session, method, target, op, body);
    // A cached organization code outlives the REST session it was minted for if the session is
    // dropped at the agent, and a client cannot tell that from a genuine refusal. Re-resolving once
    // costs two round trips on a request that has already failed; a real refusal simply fails again.
    if (cached && (response.status === 401 || response.status === 403)) {
      // Except when it is a reauthentication refusal: re-resolving cannot clear one, and the
      // session is about to end, so the retry would only repeat the failure. Left for
      // `checkedStatus` to act on.
      if (!MeshApi.isReauthentication(await MeshApi.serverErrorText(response))) {
        authService.clearOperatorSession(tokens);
        let refreshed = await this.ensureOperatorSession();
        response = await this.sendOperatorRequest(tokens, refreshed, method, target, op, body);
      }
    }
    return response;
  }

  private async sendOperatorRequest(
    tokens: SavedTokens,
    session: OperatorSession,
    method: string,
    target: 'factory' | 'human',
    op: string,
    body?: unknown,
  ): Promise<Response> {
    let urlBase = process.env.REACT_APP_MESH_API_URL;
    let url =
      `${urlBase}/entities/operator/${encodeURIComponent(session.orgLinkCode)}/${target}/${op}` +
      `?operator_proxy=${encodeURIComponent(session.operatorProxy)}`;
    return await fetch(url, {
      method,
      headers:
        body === undefined
          ? { Authorization: `Bearer ${tokens.idToken}` }
          : { Authorization: `Bearer ${tokens.idToken}`, 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  }

  private static async checkedJson(response: Response): Promise<any> {
    await MeshApi.checkedStatus(response);
    let data = await response.json();
    if (data && data.error) {
      throw new Error(`Error from server: ${data.error}`);
    }
    return data;
  }

  // The REST agent puts its reason in the body of every error response, so a bare status code
  // discards the one thing that explains the failure. Read from a clone: the caller still has the
  // body to parse, and a non-JSON error page must not mask the status behind a parse error.
  private static async serverErrorText(response: Response): Promise<string> {
    try {
      let data = await response.clone().json();
      return data && typeof data.error === 'string' ? data.error : '';
    } catch {
      return '';
    }
  }

  // Matched on the server's text rather than its status. The organization agent is the only agent
  // on an operator call's path that checks the reauthentication window - the relay onward to the
  // factory and human agents never does - and it answers 401 where `error_to_http_status` maps
  // `ReauthenticationNeeded`, 500 on a mesh that predates that arm. The text is the same on both.
  private static isReauthentication(serverText: string): boolean {
    return serverText.toLowerCase().includes('reauthentication');
  }

  static readonly REAUTHENTICATION_MESSAGE: string =
    "Reauthentication needed: this session has aged past your organization's reauthentication " +
    'window. You have been signed out - sign in again to continue.';

  private static async checkedStatus(response: Response) {
    if (response.status === 200) {
      return;
    }
    let serverText = await MeshApi.serverErrorText(response);
    // Nothing in this client can clear a reauthentication challenge, so there is no recovery to
    // offer and holding a session the mesh has stopped honouring only produces the same failure on
    // the next page. Ending it is the honest outcome.
    if (MeshApi.isReauthentication(serverText)) {
      authService.endSession(MeshApi.REAUTHENTICATION_MESSAGE);
      throw new Error(MeshApi.REAUTHENTICATION_MESSAGE);
    }
    // The organization agent decides operator authority on every call, so a 403 here is the
    // ordinary refusal - not holding the organization's principal-admin credential, or the
    // organization not holding a verified mesh-operator domain. Its body says only "Request
    // denied", so the explanation is worth more than the text.
    if (response.status === 403) {
      throw new Error(
        'Request denied (403): this session does not carry mesh-operator authority for the selected organization.',
      );
    }
    if (serverText) {
      throw new Error(`${serverText} (${response.status})`);
    }
    throw new Error(`Request failed with status ${response.status}`);
  }

  async getRotateKeysStatus(trusteeName: string): Promise<GetRotateKeysResponseData> {
    let response = await this.operatorRequest('POST', 'factory', 'get_rotate_keys_status', {
      destination_trustee_name: trusteeName,
    });
    return (await MeshApi.checkedJson(response)) as GetRotateKeysResponseData;
  }

  async rotateKeys(trusteeName: string, rotateStemCellKey: boolean, rotateTrusteeStemId: boolean): Promise<void> {
    let response = await this.operatorRequest('POST', 'factory', 'rotate_keys', {
      destination_trustee_name: trusteeName,
      rotate_stem_cell_key: rotateStemCellKey,
      rotate_trustee_stem_id: rotateTrusteeStemId,
    });
    await MeshApi.checkedJson(response);
  }

  async rotateKeystone(): Promise<void> {
    let response = await this.operatorRequest('POST', 'factory', 'rotate_keystone');
    await MeshApi.checkedStatus(response);
  }

  async populateHumanOpensearch(): Promise<void> {
    let response = await this.operatorRequest('POST', 'human', 'populate_opensearch');
    await MeshApi.checkedStatus(response);
  }

  async getConfiguration(agentName: string, storedAtParent: boolean): Promise<ConfigReplyJsonData> {
    let response = await this.operatorRequest('POST', 'factory', 'get_configuration', {
      destination_agent_name: agentName,
      stored_at_parent: storedAtParent,
    });
    return (await MeshApi.checkedJson(response)) as ConfigReplyJsonData;
  }

  async updateConfiguration(
    agentName: string,
    storeAtParent: boolean,
    name: string,
    value: string,
    isSecret: boolean,
  ): Promise<void> {
    let response = await this.operatorRequest('POST', 'factory', 'update_configuration', {
      destination_agent_name: agentName,
      store_at_parent: storeAtParent,
      config: [{ name, value: value ? value : null, is_secret: isSecret }],
    });
    await MeshApi.checkedJson(response);
  }

  async deleteHumanByEmail(email: string): Promise<void> {
    let response = await this.operatorRequest('POST', 'human', 'admin_delete', { email });
    await MeshApi.checkedJson(response);
  }

  async deleteHumanByPhoneNumber(phone: string): Promise<void> {
    let response = await this.operatorRequest('POST', 'human', 'admin_delete', { phone });
    await MeshApi.checkedJson(response);
  }

  async getDeployments(): Promise<DeploymentStatusesJsonData[]> {
    let response = await this.operatorRequest('GET', 'factory', 'deployment_status');
    return (await MeshApi.checkedJson(response)) as DeploymentStatusesJsonData[];
  }

  async getBuilds(offset: number, limit: number): Promise<GetBuildsResponseJsonData> {
    let response = await this.operatorRequest('POST', 'factory', 'get_builds', {
      offset,
      limit,
      order_descending: true,
    });
    const body = await MeshApi.checkedJson(response);
    // A pre-envelope mesh returns a bare array, so either side can deploy first.
    if (Array.isArray(body)) {
      const builds = body as BuildInformationJsonData[];
      return {
        builds,
        next_offset: builds.length === limit ? offset + limit : undefined,
      };
    }
    return body as GetBuildsResponseJsonData;
  }

  async getDebugSessions(offset: number, limit: number): Promise<DebugSessionInformationJsonData[]> {
    let response = await this.operatorRequest('POST', 'factory', 'get_debug_sessions', {
      offset,
      limit,
      order_descending: true,
    });
    return (await MeshApi.checkedJson(response)) as DebugSessionInformationJsonData[];
  }

  async resubmitBuilt(build: BuildInformationJsonData): Promise<void> {
    let response = await this.operatorRequest('POST', 'factory', 'update_build', {
      commit_sha: build.commit_sha,
      build_status: 'Pending',
      site_id: build.site_id,
      meshagent_deployable_id: build.meshagent_deployable_id,
      meshvm_deployable_id: build.meshvm_deployable_id,
    });
    await MeshApi.checkedStatus(response);
  }

  async createDebugSession(prompt: string): Promise<CreateDebugSessionResponseJsonData> {
    let response = await this.operatorRequest('POST', 'factory', 'create_debug_session', { prompt });
    return (await MeshApi.checkedJson(response)) as CreateDebugSessionResponseJsonData;
  }

  // Pending is refused by the factory agent, so a session is abandoned rather than requeued.
  async updateDebugSessionStatus(
    sessionId: string,
    status: Exclude<DebugSessionStatusJsonData, 'Pending'>,
  ): Promise<void> {
    let response = await this.operatorRequest('POST', 'factory', 'update_debug_session', {
      session_id: sessionId,
      status,
    });
    await MeshApi.checkedJson(response);
  }

  private static subjectName(certificate: x509.X509Certificate): string {
    return certificate.subjectName.getField('CN')[0];
  }

  private static issuerName(certificate: x509.X509Certificate): string {
    return certificate.issuerName.getField('CN')[0];
  }

  private static serialNumber(certificate: x509.X509Certificate): string {
    return certificate.serialNumber;
  }

  // @peculiar/x509 reports algorithms it does not recognize by OID.
  static readonly ALGORITHM_NAMES_BY_OID: Record<string, string> = {
    '2.16.840.1.101.3.4.3.17': 'ML-DSA-44',
    '2.16.840.1.101.3.4.3.18': 'ML-DSA-65',
    '2.16.840.1.101.3.4.3.19': 'ML-DSA-87',
  };

  private static certificateExportData(
    certificate: x509.X509Certificate,
    key: CertificateRecordKey,
    parent: CertificateRecordKey,
  ): CertificateRecordExportData {
    let additional_info = '';
    if ('namedCurve' in certificate.publicKey.algorithm) {
      additional_info = certificate.publicKey.algorithm.namedCurve as string;
    } else if ('modulusLength' in certificate.publicKey.algorithm) {
      additional_info = (certificate.publicKey.algorithm.modulusLength as number).toString();
    }
    let pubkey_algorithm_name = certificate.publicKey.algorithm.name;
    return {
      display_name: key.display_name,
      serial_number: key.serial_number,
      subject: MeshApi.subjectName(certificate),
      pubkey_algorithm_name: MeshApi.ALGORITHM_NAMES_BY_OID[pubkey_algorithm_name] ?? pubkey_algorithm_name,
      pubkey_algorithm_additional_info: additional_info,
      // The library types hash as always present, but algorithms such as ML-DSA sign without a separate hash.
      signature_algorithm_hash: certificate.signatureAlgorithm.hash?.name ?? '',
      creation: certificate.notBefore,
      expiration: certificate.notAfter,
      parent_display_name: parent.display_name,
      parent_serial_number: parent.serial_number,
    };
  }

  static readonly CERTIFICATE_PEM_SUFFIX: string = '-----END CERTIFICATE-----';

  static readonly MESH_ROOT_CA_COMMON_NAME: string = 'Mesh Root CA';

  private static guardianLabel(certificate: x509.X509Certificate, network_location?: string): string {
    if (network_location) {
      return `Guardian ${network_location}`;
    }
    return `Guardian ${MeshApi.subjectName(certificate).substring(0, 12)}`;
  }

  async getCertificates(): Promise<CertificateRecords> {
    let response = await this.operatorRequest('GET', 'factory', 'certificate_records');
    let records = (await MeshApi.checkedJson(response)) as CertificateRecordIntermediateData[];
    let result = { guardians: {}, end_entities: {}, intermediates: {}, root: {} } as CertificateRecords;
    records.forEach(record => {
      try {
        let raw_components = record.cert.split(MeshApi.CERTIFICATE_PEM_SUFFIX);
        let components = raw_components.slice(0, -1); // remove the trailing empty component
        let certs: x509.X509Certificate[] = components.map(component => {
          component = component + MeshApi.CERTIFICATE_PEM_SUFFIX;
          return new x509.X509Certificate(component);
        });
        if (certs.length === 0) {
          console.log(`Skipping certificate record with no certificates: ${record.display_name}`);
          return;
        }
        let parent_key: CertificateRecordKey = {
          display_name: MeshApi.issuerName(certs[0]),
          serial_number: certs.length > 1 ? MeshApi.serialNumber(certs[1]) : '-',
        };
        if (MeshApi.issuerName(certs[0]) === MeshApi.MESH_ROOT_CA_COMMON_NAME) {
          let key: CertificateRecordKey = {
            display_name: MeshApi.guardianLabel(certs[0], record.network_location),
            serial_number: MeshApi.serialNumber(certs[0]),
          };
          let string_key = keyToString(key);
          result.guardians[string_key] = MeshApi.certificateExportData(certs[0], key, parent_key);
        } else {
          let key: CertificateRecordKey = {
            display_name: record.display_name,
            serial_number: MeshApi.serialNumber(certs[0]),
          };
          let string_key = keyToString(key);
          result.end_entities[string_key] = MeshApi.certificateExportData(certs[0], key, parent_key);
        }
        for (let i = 1; i < certs.length; i++) {
          let subject_name = MeshApi.subjectName(certs[i]);
          let issuer_name = MeshApi.issuerName(certs[i]);
          let key: CertificateRecordKey = { display_name: subject_name, serial_number: MeshApi.serialNumber(certs[i]) };
          let string_key = keyToString(key);
          if (subject_name !== issuer_name && !(string_key in result.intermediates)) {
            let parent_serial_number = i === certs.length - 1 ? '-' : MeshApi.serialNumber(certs[i + 1]);
            let parent_key: CertificateRecordKey = { display_name: issuer_name, serial_number: parent_serial_number };
            result.intermediates[string_key] = MeshApi.certificateExportData(certs[i], key, parent_key);
          } else if (subject_name === issuer_name && !(string_key in result.root)) {
            let parent_key = key;
            result.root[string_key] = MeshApi.certificateExportData(certs[i], key, parent_key);
          }
        }
      } catch (error) {
        console.log(`Skipping malformed certificate record (${record.display_name}):`, error);
      }
    });
    return result;
  }
}

const api = new MeshApi();
export default api;
